import { isFieldEmpty } from '@yoweb/prismic-slicemachine/utils/isFieldEmpty';
import type { SliceTemplateProps } from '@yoweb/prismic-slicemachine/slices/slicesTypes';
import { CornerCardSectionSliceType } from '@yoweb/prismic-slicemachine/slices/CornerCardSectionSlice';
import { DividerSliceType } from '@yoweb/prismic-slicemachine/slices/DividerSlice';
import { ExplanationSectionSliceType } from '@yoweb/prismic-slicemachine/slices/ExplanationSectionSlice';
import { SeoSliceType } from '@yoweb/prismic-slicemachine/slices/SeoSlice';
import { VerticalScrollCarouselSliceType } from '@yoweb/prismic-slicemachine/slices/VerticalScrollCarouselSlice';
import { HeroSectionSliceType } from '@yoweb/prismic-slicemachine/slices/HeroSectionSlice';
import { SmallPrintSliceType } from '@yoweb/prismic-slicemachine/slices/SmallPrintSlice';
import { useViewport } from '@yoweb/ui/hooks/useViewport';
import { Box } from '@yoweb/ui/components/Box';
import { MarketingCarouselSliceType } from '@yoweb/prismic-slicemachine/slices/MarketingCarouselSlice';
import { ParagraphSliceType } from '@yoweb/prismic-slicemachine/slices/ParagraphSlice';
import { AnimateWhenInView } from '@yoweb/ui/components/AnimateIn';
import SeoSlice from '@yoweb/prismic-slicemachine/slices/SeoSlice/SeoSlice';
import HeroSectionSlice from '@yoweb/prismic-slicemachine/slices/HeroSectionSlice/HeroSectionSlice';
import SmallPrintSlice from '@yoweb/prismic-slicemachine/slices/SmallPrintSlice/SmallPrintSlice';
import DividerSlice from '@yoweb/prismic-slicemachine/slices/DividerSlice/DividerSlice';
import ExplanationSectionSlice from '@yoweb/prismic-slicemachine/slices/ExplanationSectionSlice/ExplanationSectionSlice';
import CornerCardSectionSlice from '@yoweb/prismic-slicemachine/slices/CornerCardSectionSlice/CornerCardSectionSlice';
import VerticalScrollCarouselSlice from '@yoweb/prismic-slicemachine/slices/VerticalScrollCarouselSlice/VerticalScrollCarouselSlice';
import MarketingCarouselSlice from '@yoweb/prismic-slicemachine/slices/MarketingCarouselSlice/MarketingCarouselSlice';
import ParagraphSlice from '@yoweb/prismic-slicemachine/slices/ParagraphSlice/ParagraphSlice';
import LargeVideoSectionSlice from '@yoweb/prismic-slicemachine/slices/LargeVideoSectionSlice/LargeVideoSectionSlice';
import { LargeVideoSectionSliceType } from '@yoweb/prismic-slicemachine/slices/LargeVideoSectionSlice';
import TestimonialSectionSlice, {
  TestimonialSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/TestimonialSectionSlice';
import CardCarouselSectionSlice, {
  CardCarouselSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/CardCarouselSectionSlice';
import { ImageWithTitleCard } from '@yoweb/ui/components/marketing/ImageWithTitleCard';
import FaqSlice, { FaqSliceType } from '@yoweb/prismic-slicemachine/slices/FaqSlice';
import ContentImageSectionSlice, {
  ContentImageSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/ContentImageSectionSlice';
import ColoredCtaSectionSlice, {
  ColoredCtaSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/ColoredCtaSectionSlice';
import AppDownloadCtaSectionSlice, {
  AppDownloadCtaSectionSliceType,
} from '@yoweb/prismic-slicemachine/slices/AppDownloadCtaSectionSlice';
import MediaHeaderSlice, {
  MediaHeaderSliceType,
} from '@yoweb/prismic-slicemachine/slices/MediaHeaderSlice';
import DreamGallerySlice, {
  DreamGallerySliceType,
} from '@yoweb/prismic-slicemachine/slices/DreamGallerySlice';

export const EmployersTemplate = ({ slice }: SliceTemplateProps) => {
  const { viewportWidth } = useViewport();

  switch (slice.slice_type) {
    case SeoSliceType:
      return <SeoSlice slice={slice} />;

    case HeroSectionSliceType:
      return (
        <HeroSectionSlice
          slice={slice}
          mobilePosition="70% 70%"
          withCard={viewportWidth < 1024 ? false : true}
          contentMaxWidth={{ md: 448 }}
        />
      );

    case SmallPrintSliceType:
      return (
        <Box backgroundColor={{ _: 'base050', lg: 'base000' }} pb="medium1">
          <SmallPrintSlice slice={slice} />
        </Box>
      );

    case DividerSliceType:
      return <DividerSlice slice={slice} />;

    case ExplanationSectionSliceType:
      if (isFieldEmpty(slice.primary?.backgroundColor)) {
        slice.primary.backgroundColor = { _: 'base050', lg: 'base000' };
      }

      return (
        <ExplanationSectionSlice
          slice={slice}
          pt={{ _: 'large3', md: 'large2', lg: 104 }}
          data-testid="explanation-section"
        />
      );

    case CornerCardSectionSliceType:
      return <CornerCardSectionSlice slice={slice} />;

    case VerticalScrollCarouselSliceType:
      return <VerticalScrollCarouselSlice slice={slice} />;

    case MarketingCarouselSliceType:
      return (
        <AnimateWhenInView>
          <MarketingCarouselSlice slice={slice} />
        </AnimateWhenInView>
      );

    case ParagraphSliceType:
      return <ParagraphSlice slice={slice} />;

    case LargeVideoSectionSliceType:
      return <LargeVideoSectionSlice slice={slice} />;

    case TestimonialSectionSliceType:
      return <TestimonialSectionSlice slice={slice} />;

    case FaqSliceType:
      return <FaqSlice slice={slice} />;

    case ContentImageSectionSliceType:
      return <ContentImageSectionSlice slice={slice} />;

    case CardCarouselSectionSliceType:
      return (
        <CardCarouselSectionSlice id={`carousel-section-${slice.primary.title}`} slice={slice}>
          {slice.items.map(({ image, cardId, title: itemTitle }) => (
            <ImageWithTitleCard
              key={cardId}
              image={image?.url ?? ''}
              imageAltText={image?.alt ?? ''}
              title={itemTitle}
            />
          ))}
        </CardCarouselSectionSlice>
      );

    case ColoredCtaSectionSliceType:
      return <ColoredCtaSectionSlice slice={slice} />;

    case AppDownloadCtaSectionSliceType:
      return <AppDownloadCtaSectionSlice slice={slice} />;

    case MediaHeaderSliceType:
      return <MediaHeaderSlice slice={slice} />;

    case DreamGallerySliceType:
      return <DreamGallerySlice slice={slice} />;

    default:
      return null;
  }
};
